import { useQuery } from "@tanstack/react-query";
import { BrainCircuit } from "lucide-react";
import { useCallback, useState } from "react";
import { Modal } from "../../components/ui/modal";
import { core } from "../../lib/api";
import { AgentTestWorkspace } from "../agents/agent-test-workspace";
import { WorkflowTestWorkspace } from "../agents/workflow-test-workspace";
import type { StudioVersion } from "../agents/studio-editors";
import type { Agent } from "./types";

interface WorkflowDocument {
  version_id?: string;
  version_name?: string;
  call_infields_id?: string;
  call_infields?: Record<string, unknown>[];
}

export function TestCallModal({ agent, onClose }: { agent: Agent; onClose: () => void }) {
  const agentName = agent.agent_name || agent.name || "Untitled Agent";
  const workflowId = agent.multi_agent_config_id || "";
  const thinkerAndTalker = agent.agent_type?.toLowerCase() === "thinker_talker";

  const versions = useQuery({
    queryKey: ["agents", agent.agent_id, "test-versions"],
    queryFn: () => core<StudioVersion[]>(
      `/api/v1/agents/${encodeURIComponent(agent.agent_id)}/versions/summary?limit=200`,
    ),
    enabled: !workflowId,
    staleTime: 60_000,
  });

  const currentVersion = useQuery({
    queryKey: ["agents", agent.agent_id, "test-current-version"],
    queryFn: () => core<StudioVersion>(
      `/api/v1/agents/${encodeURIComponent(agent.agent_id)}/versions/current`,
    ),
    enabled: !workflowId,
    staleTime: 60_000,
  });

  const workflow = useQuery({
    queryKey: ["workflows", workflowId, "test-document"],
    queryFn: () => core<WorkflowDocument>(
      `/api/v1/multi-agent-prompts/${encodeURIComponent(workflowId)}`,
    ),
    enabled: Boolean(workflowId),
    staleTime: 60_000,
  });

  const ensureVersions = useCallback(
    async () => versions.data || [],
    [versions.data],
  );

  const loadVersionDetails = useCallback((versionId: string) => core<StudioVersion>(
    `/api/v1/agents/${encodeURIComponent(agent.agent_id)}/versions/${encodeURIComponent(versionId)}`,
  ), [agent.agent_id]);

  const loading = workflowId
    ? workflow.isLoading
    : versions.isLoading || currentVersion.isLoading;
  const failed = workflowId
    ? workflow.isError
    : versions.isError;
  const activeVersionId = currentVersion.data?.version_id || versions.data?.[0]?.version_id || "";

  return (
    <Modal
      title={(
        <span className="inline-flex items-center gap-2">
          {thinkerAndTalker && <BrainCircuit className="h-5 w-5 text-brand" />}
          Test {agentName}
        </span>
      )}
      subtitle="Choose a published version, provide contact data, then start a web or telephony test."
      onClose={onClose}
      width="max-w-[min(1200px,calc(100vw-2rem))]"
    >
      {loading ? (
        <div className="h-72 animate-pulse rounded-md bg-soft" />
      ) : failed ? (
        <div className="rounded-md border border-danger/30 bg-danger-soft p-4 text-sm text-danger">
          Test-call configuration could not be loaded.
        </div>
      ) : workflowId ? (
        <WorkflowTestWorkspace
          agentId={agent.agent_id}
          agentName={agentName}
          architecture={thinkerAndTalker ? "thinker_talker" : "single"}
          workflowConfigId={workflowId}
          workflowVersionId={workflow.data?.version_id || ""}
          workflowVersionName={workflow.data?.version_name || ""}
        />
      ) : (
        <AgentTestWorkspace
          agentId={agent.agent_id}
          agentName={agentName}
          architecture={thinkerAndTalker ? "thinker_talker" : "single"}
          activeVersionId={activeVersionId}
          versions={versions.data || []}
          isNew={false}
          dirty={false}
          ensureVersions={ensureVersions}
          loadVersionDetails={loadVersionDetails}
        />
      )}
    </Modal>
  );
}
